import React from "react";
import { BalanceSheetMetricCard } from "./BalanceSheetMetricCard";
import { BalanceSheetMetricCardSkeleton } from "./BalanceSheetMetricCardSkeleton";
import { BalanceSheetSummaryInsights } from "./BalanceSheetSummaryInsights";
import { formatPlAmount } from "../../../client_dashboard/presentation/utils/formatPlAmount";

export interface BalanceSheetSummaryCardData {
  key: string;
  label: string;
  value: number;
  value_formatted: string;
  previous_value: number;
  trend: "up" | "down" | "neutral";
  trend_text: string;
  change_percent: number | null;
}

export interface BalanceSheetSummarySectionProps {
  cards: BalanceSheetSummaryCardData[] | null;
  isLoading: boolean;
  error: string;
  /** Shown on each metric card (e.g. "31st March 2025"); from `formatEffectiveDateLongOrdinal` / client effective date. */
  asOfLabel?: string;
}

function formatCardValue(card: BalanceSheetSummaryCardData): string {
  const key = card.key ?? "";
  const value = Number(card.value ?? 0);
  const isRatio = key.includes("ratio") || key === "debt_to_equity";
  if (isRatio) return value.toFixed(2);
  return formatPlAmount(value);
}

const CARD_KEYS_ORDER = [
  "cash_and_bank",
  "working_capital",
  "deferred_revenue",
  "current_ratio",
  "quick_ratio",
  "debt_to_equity",
];

export function BalanceSheetSummarySection({
  cards,
  isLoading,
  error,
  asOfLabel,
}: BalanceSheetSummarySectionProps) {
  const orderedCards =
    cards &&
    CARD_KEYS_ORDER.map((key) => cards.find((c) => c.key === key)).filter(
      (c): c is BalanceSheetSummaryCardData => c != null,
    );

  const formulaTooltipByKey: Record<string, string> = {
    cash_and_bank: "Includes all balances",
    working_capital:
      "Working Capital = Current Assets − Current Liabilities\n\nCurrent Assets = Cash & Bank + Trade Receivables + Loans & Advances + Inventory + Other Current Assets\nCurrent Liabilities = |Trade Payables + Liabilities & Provisions + Deferred Revenue|",
    current_ratio:
      "Current Ratio = Current Assets / Current Liabilities\n\nCurrent Assets = Cash & Bank + Trade Receivables + Loans & Advances + Inventory + Other Current Assets\nCurrent Liabilities = |Trade Payables + Liabilities & Provisions + Deferred Revenue|",
    quick_ratio:
      "Quick Ratio = (Current Assets − Inventory) / Current Liabilities\n\nCurrent Assets = Cash & Bank + Trade Receivables + Loans & Advances + Inventory + Other Current Assets\nCurrent Liabilities = |Trade Payables + Liabilities & Provisions + Deferred Revenue|",
    debt_to_equity:
      "Debt-to-Equity = Total Debt / Total Equity\n\nTotal Debt = |Borrowings|\nTotal Equity = Share Capital + Reserves and Surplus",
  };

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full items-stretch gap-4 bg-white">
        {isLoading &&
          Array.from({ length: 6 }).map((_, i) => (
            <BalanceSheetMetricCardSkeleton key={i} />
          ))}
        {!isLoading && error && (
          <div className="w-full text-body font-body text-subtext-color py-2">
            {error}
          </div>
        )}
        {!isLoading && !error && orderedCards && orderedCards.length > 0 && (
          <>
            {orderedCards.map((card) => (
              <BalanceSheetMetricCard
                key={card.key}
                label={card.label}
                value={formatCardValue(card)}
                asOfLabel={asOfLabel}
                trend={card.trend}
                trendText={card.trend_text}
                infoTooltip={formulaTooltipByKey[card.key]}
              />
            ))}
          </>
        )}
        {!isLoading &&
          !error &&
          (!orderedCards || orderedCards.length === 0) && (
            <div className="text-caption font-caption text-subtext-color py-2">
              No summary data. Select a month and apply, or ensure client
              monthly data is populated.
            </div>
          )}
      </div>
      <BalanceSheetSummaryInsights />
    </div>
  );
}
